import classnames from 'classnames'

import { useBodyNoScroll } from '@src/device/hooks/useBodyNoScroll'
import {
  Filter,
  FilterKeys,
  SelectedFilter,
  UpdateQueryParams,
} from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'

import Button, { BUTTON_SIZE, BUTTON_TYPES } from '../../button'
import Modal from '../../modal'
import Select from '../../select'
import { ROW_SELECTOR_PLACEHOLDERS, SORT_BY, SORT_OPTIONS } from '../constants/constants'
import Cross from '../icons/cross.inline.svg'

import styles from './filterModal.module.scss'

export interface FilterModalProps {
  open: boolean
  onCancel: () => void
  className?: string
  filters: Filter[]
  noOfItems: number
  selectedFilters: SelectedFilter
  updateQuery: (params: UpdateQueryParams) => void
  showSelectedOptionOnDropDown?: boolean
  skipSorting?: boolean
  sortBy?: string
}

const FilterModal = ({ filters = [], open = false, ...props }: FilterModalProps) => {
  const classes = classnames(styles.modal, props.className)
  useBodyNoScroll(open)

  return (
    <Modal
      open={open}
      className={classes}
      showCloseButton
      onClose={props.onCancel}
      crossAlignment="left"
      closeButtonClassName={styles.modal__close}
    >
      <div className={styles.modal__header}>
        <div className={styles.modal__header__text}>{props.noOfItems} ITEMS</div>
      </div>
      <div>
        <div className={styles.modal__content}>
          {filters
            .filter(x => x.values.length > 0)
            .map(filter => {
              return (
                <Select
                  id={filter.name}
                  key={filter.config.label}
                  className={styles.modal__content__filter}
                  placeholder={filter.config.label}
                  options={filter.values.map(x => Filter.makeSelectFilterOptionFromValue(x))}
                  showMultipleColumns={ROW_SELECTOR_PLACEHOLDERS.includes(filter.config.label.toUpperCase())}
                  OnOptionClick={(_e, optionValue) => {
                    props.updateQuery({
                      filter: {
                        type: 'toggle',
                        value: optionValue,
                        name: filter.name as FilterKeys,
                        label: filter.config.label,
                      },
                    })
                  }}
                  selectedOptions={SelectedFilter.getSelectedFilterValuesByFilterName(
                    props.selectedFilters,
                    filter.name as keyof SelectedFilter,
                  )}
                />
              )
            })}
          {!props.skipSorting ? (
            <Select
              id="Sort"
              placeholder="Sort"
              options={SORT_OPTIONS}
              OnOptionClick={(_e, value) => {
                props.updateQuery({ sort: { type: 'toggle', value } })
                /** NOTE: If this causes issues - check with BE team if they still need Sort key in filters otherwise clear this code */
                // props.onSortClick(value)
                // const currentSortFiltersFromState: string[] = SelectedFilter.getSelectedFilterValuesByFilterName(
                //   props.selectedFilters,
                //   'sort',
                // )
                // const selectedSortOption = SORT_OPTIONS.find(option => option.value === value)
                // const { label: selectedSortOptionLabel } = selectedSortOption || { label: '' }

                // if (currentSortFiltersFromState.includes(selectedSortOptionLabel)) {
                //   props.onSelectedFiltersChange(
                //     SelectedFilter.removeValueFromSelectedFilter(props.selectedFilters, selectedSortOptionLabel),
                //   )
                // } else {
                //   props.onSelectedFiltersChange(
                //     SelectedFilter.addValueToSelectedFilter(props.selectedFilters, 'sort', selectedSortOptionLabel),
                //   )
                // }
              }}
              className={styles.modal__content__filter}
              showSelectedOptionOnDropDown={props.showSelectedOptionOnDropDown}
            />
          ) : null}
        </div>
        {(SelectedFilter.getSelectedValuesAsList(props.selectedFilters).length ||
          props.sortBy !== SORT_BY.FEATURED.value) && (
          <div className={styles.modal__content__filterValues}>
            {Object.entries(props.selectedFilters).flatMap(([filterName, filterValues]) => {
              if (!filterValues) return []
              return filterValues.map((filterValueItem: FilterKeys) => (
                <div key={`${filterName}-${filterValueItem}`} className={styles.modal__content__filterValues__value}>
                  <div className={styles.modal__content__filterValues__value__text}>{filterValueItem}</div>
                  <Cross
                    width={8}
                    onClick={() => {
                      props.updateQuery({
                        filter: { type: 'remove', name: filterName as FilterKeys, value: filterValueItem },
                      })
                    }}
                  />
                </div>
              ))
            })}
            {props.sortBy && props.sortBy !== SORT_BY.FEATURED.value && Object.hasOwn(SORT_BY, props.sortBy) && (
              <div key={`sort-${props.sortBy}`} className={styles.modal__content__filterValues__value}>
                <div
                  className={styles.modal__content__filterValues__value__text}
                >{`Sort: ${SORT_BY[props.sortBy].label}`}</div>
                <Cross
                  width={8}
                  onClick={() => {
                    props.updateQuery({ sort: { type: 'reset' } })
                  }}
                />
              </div>
            )}
          </div>
        )}
        <Button
          className={styles.modal__content__button}
          type={BUTTON_TYPES.SECONDARY}
          size={BUTTON_SIZE.MEDIUM}
          disabled={false}
          round={true}
          onClick={() => {
            props.onCancel()
          }}
        >
          SHOW ME THE GOODS
        </Button>
        {(SelectedFilter.getSelectedValuesAsList(props.selectedFilters).length > 0 ||
          (props.sortBy && props.sortBy !== SORT_BY.FEATURED.value)) && (
          <div
            onClick={() => {
              props.updateQuery({ filter: { type: 'reset' }, sort: { type: 'reset' } })
            }}
            className={styles.modal__content__clear}
          >
            <span className={styles.modal__content__clear__text}>CLEAR ALL FILTERS</span>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default FilterModal
